import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable, split } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { invariant } from "ts-invariant";
import "cross-fetch/polyfill";
import * as Sentry from "@sentry/core";
export { HttpLink };
export function makeClient(config) {
    var connectToDevTools = config.connectToDevTools, request = config.request, uri = config.uri, errorCallback = config.onError, defaultOptions = config.defaultOptions, preHttpLink = config.preHttpLink, subscriptionOptions = config.subscriptionOptions, headers = config.headers, isProduction = config.isProduction, baseClass = config.baseClass;
    // https://github.com/Haegin/apollo-sentry-link/blob/master/src/index.js
    var operationInfo = function(operation) {
        var _operation_getContext_headers, _operation_getContext_headers1;
        var now = Math.floor(Date.now() / 1000);
        var requestID = (_operation_getContext_headers = operation.getContext().headers) === null || _operation_getContext_headers === void 0 ? void 0 : _operation_getContext_headers['X-RequestID'];
        var hasBearerToken = !!((_operation_getContext_headers1 = operation.getContext().headers) === null || _operation_getContext_headers1 === void 0 ? void 0 : _operation_getContext_headers1['authorization']);
        var _operation_variables;
        var variables = isProduction ? undefined : JSON.stringify((_operation_variables = operation.variables) !== null && _operation_variables !== void 0 ? _operation_variables : {});
        return {
            requestID: requestID,
            type: operation.query.definitions.find(function(defn) {
                return defn.operation;
            }).operation,
            name: operation.operationName,
            dateNow: now,
            hasBearerToken: hasBearerToken,
            variables: variables
        };
    };
    var cache = config.cache;
    if (!cache) {
        cache = new InMemoryCache();
    }
    var errorLink = errorCallback ? onError(errorCallback) : onError(function(param) {
        var graphQLErrors = param.graphQLErrors, networkError = param.networkError;
        if (graphQLErrors) {
            graphQLErrors.map(function(param) {
                var message = param.message, locations = param.locations, path = param.path;
                return(// tslint:disable-next-line
                invariant.warn("[GraphQL error]: Message: ".concat(message, ", Location: ") + "".concat(locations, ", Path: ").concat(path)));
            });
        }
        if (networkError) {
            // tslint:disable-next-line
            invariant.warn("[Network error]: ".concat(networkError));
        }
    });
    var requestHandler = request ? new ApolloLink(function(operation, forward) {
        return new Observable(function(observer) {
            var handle;
            Promise.resolve(operation).then(function(oper) {
                return request(oper);
            }).then(function() {
                handle = forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer)
                });
            }).catch(observer.error.bind(observer));
            return function() {
                if (handle) {
                    handle.unsubscribe();
                }
            };
        });
    }) : false;
    var httpLink = new HttpLink({
        uri: uri || '/graphql',
        fetch: fetch,
        fetchOptions: {},
        credentials: 'same-origin',
        headers: headers !== null && headers !== void 0 ? headers : {}
    });
    if (subscriptionOptions && // don't use subscriptions in SSR
    global.window) {
        var wsLink = new GraphQLWsLink(createClient({
            url: subscriptionOptions.uri,
            lazy: true,
            lazyCloseTimeout: 30000,
            connectionParams: subscriptionOptions.connectionParams,
            shouldRetry: function() {
                return true;
            }
        }));
        httpLink = split(// split based on operation type
        function(param) {
            var query = param.query;
            var definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        }, wsLink, preHttpLink ? ApolloLink.from([
            preHttpLink,
            httpLink
        ]) : httpLink);
    }
    // https://github.com/Haegin/apollo-sentry-link/blob/master/src/index.js
    var sentryLink = new ApolloLink(function(operation, forward) {
        Sentry.addBreadcrumb({
            type: 'http',
            category: 'graphql',
            data: operationInfo(operation),
            level: 'debug'
        });
        return forward(operation);
    });
    var retryLink = new RetryLink({
        attempts: {
            max: 3
        }
    });
    var link = ApolloLink.from([
        removeTypenameFromVariables(),
        errorLink,
        retryLink,
        requestHandler,
        sentryLink,
        httpLink
    ].filter(function(x) {
        return !!x;
    }));
    var _parentClass = baseClass !== null && baseClass !== void 0 ? baseClass : ApolloClient;
    return new _parentClass({
        connectToDevTools: connectToDevTools,
        cache: cache,
        link: link,
        defaultOptions: defaultOptions
    });
}
